import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoginMutation, useLogoutMutation } from '@features/auth/api';
import { useCurrentUser } from './useCurrentUser';
import { useIsAuthenticated } from './useIsAuthenticated';
import type { User } from '@features/auth/types';
import { reportMutationError } from '@/lib/mutationError';

interface UseAuthResult {
  user: User | null;
  isAuthenticated: boolean;
  isSigningIn: boolean;
  signInError: string | null;
  signIn: (username: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
}

/**
 * Façade hook over the auth slice + auth API. Page components should use
 * this — not the raw RTK Query hooks — so the wiring (token storage, slice
 * updates, redirects) stays in one place.
 */
export function useAuth(): UseAuthResult {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const [login, { isLoading: isSigningIn, error: loginError }] = useLoginMutation();
  const [logout] = useLogoutMutation();

  const signIn = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      try {
        await login({ username, password }).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [login],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await logout().unwrap().catch(reportMutationError);
    navigate('/signin', { replace: true });
  }, [logout, navigate]);

  const signInError =
    loginError && typeof loginError === 'object' && 'message' in loginError
      ? (loginError as { message?: string }).message ?? null
      : null;

  return {
    user,
    isAuthenticated,
    isSigningIn,
    signInError,
    signIn,
    signOut,
  };
}
